import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Eye, EyeSlash, GoogleLogo } from "@phosphor-icons/react";

const ID_TYPES = ["National ID (NIN)", "Driver's Licence", "International Passport", "Voter's Card"];

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function startGoogleSignin() {
  const redirectUrl = window.location.origin + "/auth/callback";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const refCode = sp.get("ref") || "";
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", date_of_birth: "", address: "",
    country: "Nigeria", id_type: "", id_number: "", password: "",
    referred_by_code: refCode,
  });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setLoading(true);
    const r = await register(form);
    setLoading(false);
    if (r.ok) {
      toast.success("Welcome to Mayoto — a confirmation link has been sent to your email.", { duration: 6000 });
      toast.info("Didn't get it in 5 min? Check spam, or from your dashboard tap 'Resend link'. Still stuck? Email mayotonexo@gmail.com and we'll verify you manually.", { duration: 9000 });
      nav("/dashboard");
    } else { toast.error(r.error); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#f8fafc]">
      <div className="hidden lg:block relative">
        <img src="https://images.unsplash.com/photo-1562910859-be83f1df7b56?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxuaWdlcmlhbiUyMGVudHJlcHJlbmV1cnMlMjBtZWV0aW5nfGVufDB8fHx8MTc4NTUzOTg0Mnww&ixlib=rb-4.1.0&q=85" alt="Register visual" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0a1128]/70" />
        <div className="absolute inset-0 p-12 flex flex-col justify-between text-white">
          <Link to="/" className="flex items-center gap-2">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-white/10"><span className="text-[#00b4d8] font-black text-2xl font-display">M</span></span>
            <span className="font-display font-black text-2xl">MAYOTO</span>
          </Link>
          <div>
            <p className="text-[#d4af37] font-display font-bold tracking-[0.25em] text-xs">CREATE INVESTOR ACCOUNT</p>
            <h1 className="mt-3 font-display font-black text-5xl leading-tight max-w-md">Join a serious investment community.</h1>
            <p className="mt-4 text-slate-300 max-w-md">Registration takes minutes. Verification comes later, when you're ready to participate.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12 overflow-y-auto">
        <div className="w-full max-w-xl py-6">
          <Link to="/" className="lg:hidden flex items-center gap-2 mb-8">
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-[#0a1128]"><span className="text-[#00b4d8] font-black text-xl font-display">M</span></span>
            <span className="font-display font-black text-xl text-[#0a1128]">MAYOTO</span>
          </Link>
          <h2 className="font-display font-black text-3xl text-[#0a1128]" data-testid="register-title">Create Investor Account</h2>
          <p className="mt-2 text-slate-600">Register to explore Mayoto's opportunities.</p>
          {refCode && (
            <div className="mt-4 rounded-xl bg-[#00b4d8]/10 border border-[#00b4d8]/30 px-4 py-2 text-sm text-[#0a1128]" data-testid="register-ref-banner">
              You were invited by referral code <span className="font-display font-bold text-[#00b4d8]">{refCode}</span>
            </div>
          )}

          <Button
            type="button"
            onClick={startGoogleSignin}
            className="mt-6 w-full bg-white text-[#0a1128] border border-slate-300 hover:bg-slate-50 rounded-full py-6 text-base font-semibold flex items-center justify-center gap-3"
            data-testid="register-google"
          >
            <GoogleLogo size={20} weight="bold" className="text-[#00b4d8]" />
            Sign up with Google
          </Button>

          <div className="flex items-center gap-3 my-6" aria-hidden="true">
            <div className="h-px bg-slate-200 flex-1" />
            <span className="text-xs text-slate-400 tracking-wider">OR SIGN UP WITH EMAIL</span>
            <div className="h-px bg-slate-200 flex-1" />
          </div>

          <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4" data-testid="register-form">
            <div className="sm:col-span-2"><Label>Full Name</Label><Input required value={form.full_name} onChange={e => set("full_name", e.target.value)} data-testid="register-name" /></div>
            <div><Label>Email</Label><Input required type="email" value={form.email} onChange={e => set("email", e.target.value)} data-testid="register-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => set("phone", e.target.value)} data-testid="register-phone" /></div>
            <div><Label>Date of Birth</Label><Input type="date" value={form.date_of_birth} onChange={e => set("date_of_birth", e.target.value)} data-testid="register-dob" /></div>
            <div><Label>Country</Label><Input value={form.country} onChange={e => set("country", e.target.value)} data-testid="register-country" /></div>
            <div className="sm:col-span-2"><Label>Address</Label><Input value={form.address} onChange={e => set("address", e.target.value)} data-testid="register-address" /></div>
            <div>
              <Label>Identification Type</Label>
              <Select value={form.id_type} onValueChange={v => set("id_type", v)}>
                <SelectTrigger data-testid="register-id-type"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent className="bg-white">
                  {ID_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Identification Number</Label><Input value={form.id_number} onChange={e => set("id_number", e.target.value)} data-testid="register-id-number" /></div>
            <div className="sm:col-span-2">
              <Label>Password</Label>
              <div className="relative">
                <Input type={show ? "text" : "password"} required value={form.password} onChange={e => set("password", e.target.value)} data-testid="register-password" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {show ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">Minimum 8 characters.</p>
            </div>
            <p className="sm:col-span-2 text-xs text-slate-500 leading-relaxed">
              By registering, you agree to Mayoto's <Link to="/legal/terms" className="text-[#00b4d8] underline">Terms & Conditions</Link>, <Link to="/legal/privacy" className="text-[#00b4d8] underline">Privacy Policy</Link>, and <Link to="/legal/risk" className="text-[#00b4d8] underline">Risk Disclosure</Link>.
            </p>
            <div className="sm:col-span-2">
              <Button type="submit" disabled={loading} className="btn-shine w-full bg-[#00b4d8] hover:bg-[#0096c7] rounded-full py-6 text-base" data-testid="register-submit">
                {loading ? "Creating…" : "Create Account"}
              </Button>
            </div>
          </form>

          <p className="mt-6 text-sm text-slate-600 text-center">
            Already have an account? <Link to="/login" className="text-[#00b4d8] font-semibold hover:underline" data-testid="register-goto-login">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
